import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useSettings } from '../../contexts/SettingsContext';
import supabase from '../../utils/supabaseClient';
import { priceConsultUrl } from '../../utils/whatsapp';
import { DEFAULT_THUMB_URL, DEFAULT_PRODUCT_THUMB_URL } from '../../utils/storage';
import './ProductDetail.css';

function ProductDetail({ lang = 'es' }) {
  const { subfamiliaId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { catalogueMode } = useSettings();
  
  // States
  const [subfamilia, setSubfamilia] = useState(null);
  const [familia, setFamilia] = useState(null);
  const [productos, setProductos] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedTalla, setSelectedTalla] = useState('');
  const [loading, setLoading] = useState(true);
  const [mainImage, setMainImage] = useState('');
  const [productImages, setProductImages] = useState([]);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [availableInventory, setAvailableInventory] = useState(0);
  const [inventoryMap, setInventoryMap] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [user, setUser] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [fullscreenGalleryIndex, setFullscreenGalleryIndex] = useState(null);
    // Handler to open/close gallery modal
    const openGallery = () => setShowGallery(true);
    const closeGallery = () => setShowGallery(false);

    // Handlers for gallery fullscreen
    const openGalleryFullscreen = (idx) => setFullscreenGalleryIndex(idx);
    const closeGalleryFullscreen = () => setFullscreenGalleryIndex(null);
    const nextGalleryImage = () => {
      if (fullscreenGalleryIndex === null) return;
      setFullscreenGalleryIndex((prev) => (prev + 1) % currentProductImages.length);
    };
    const prevGalleryImage = () => {
      if (fullscreenGalleryIndex === null) return;
      setFullscreenGalleryIndex((prev) => (prev - 1 + currentProductImages.length) % currentProductImages.length);
    };
  
  // Check if device is mobile
  const isMobile = () => {
    return window.innerWidth <= 768;
  };
  
  // Mouse tracking for zoom effect (desktop only)
  const handleMouseMove = (e) => {
    if (isMobile() || !isZoomed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomOrigin({ x, y });
  };
  
  const toggleZoom = () => {
    if (isMobile()) return;
    setIsZoomed(!isZoomed);
    if (isZoomed) {
      setZoomOrigin({ x: 50, y: 50 });
    }
  };
  
  // Touch/Click events for mobile fullscreen
  const handleImageClick = (e) => {
    // Don't open fullscreen if clicking on a button
    if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
      return;
    }
    
    if (isMobile()) {
      setShowFullscreen(true);
      document.body.style.overflow = 'hidden'; // Prevent body scroll
    }
  };
  
  // Touch swipe detection for main image
  const [mainImageTouchStart, setMainImageTouchStart] = useState(null);
  const [mainImageTouchEnd, setMainImageTouchEnd] = useState(null);
  const [mainImageTouchStartTime, setMainImageTouchStartTime] = useState(null);

  const handleTouchStart = (e) => {
    // Don't open fullscreen if touching a button
    if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
      return;
    }
    
    if (isMobile() && e.touches.length === 1) {
      // Store touch start position and time for swipe detection
      setMainImageTouchStart(e.touches[0].clientX);
      setMainImageTouchEnd(null);
      setMainImageTouchStartTime(Date.now());
    }
  };
  
  const handleTouchMove = (e) => {
    if (isMobile() && e.touches.length === 1) {
      setMainImageTouchEnd(e.touches[0].clientX);
    }
  };
  
  const handleTouchEnd = () => {
    if (!mainImageTouchStart || !mainImageTouchEnd) {
      // No swipe detected, just reset states
      setMainImageTouchStart(null);
      setMainImageTouchEnd(null);
      return;
    }
    
    // Calculate swipe distance
    const distance = mainImageTouchStart - mainImageTouchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe && currentProductImages.length > 1) {
      // Swipe left - next image
      const nextIndex = currentImageIndex === currentProductImages.length - 1 ? 0 : currentImageIndex + 1;
      setCurrentImageIndex(nextIndex);
      setMainImage(currentProductImages[nextIndex].url);
    } else if (isRightSwipe && currentProductImages.length > 1) {
      // Swipe right - previous image
      const prevIndex = currentImageIndex === 0 ? currentProductImages.length - 1 : currentImageIndex - 1;
      setCurrentImageIndex(prevIndex);
      setMainImage(currentProductImages[prevIndex].url);
    }
    
    // Reset touch states
    setMainImageTouchStart(null);
    setMainImageTouchEnd(null);
  };
  
  // Fullscreen modal handlers
  const closeFullscreen = () => {
    setShowFullscreen(false);
    document.body.style.overflow = 'auto'; // Restore body scroll
  };
  
  // Handle escape key to close fullscreen
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && showFullscreen) {
        closeFullscreen();
      }
    };
    
    if (showFullscreen) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [showFullscreen]);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (!error) {
          setUser(user);
        }
      } catch (error) {
        console.error('Error checking auth:', error);
      }
    };

    checkAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user || null);
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []);
  
  // Download image function (only for authenticated users)
  const handleDownloadImage = async () => {
    if (!user) {
      setToastMessage('Debes iniciar sesión para descargar imágenes');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }

    if (!mainImage) {
      setToastMessage('No hay imagen disponible para descargar');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }

    setDownloading(true);
    try {
      // Fetch the image
      const response = await fetch(mainImage);
      if (!response.ok) throw new Error('Error al descargar la imagen');
      
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename based on product info
      const productName = selectedProduct?.detalle || selectedProduct?.nombre || subfamilia?.nombre || 'producto';
      const color = selectedColor || 'sin-color';
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `${productName}_${color}_${timestamp}.webp`;
      
      // Sanitize filename for download
      link.download = filename.replace(/[^a-zA-Z0-9\-_.]/g, '_');
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setToastMessage('Imagen descargada correctamente');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (error) {
      console.error('Error downloading image:', error);
      setToastMessage('Error al descargar la imagen');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } finally {
      setDownloading(false);
    }
  };
  
  // Translations
  const translations = {
    es: {
      color: "Color",
      size: "Talla",
      price: "Precio",
      customize: "Personalizar",
      retailPrice: "Precio Minorista",
      wholesalePrice: "Precio Mayorista (12+)",
      specialPrice: "Precio Especial (24+)",
      addToCart: "Agregar al Carrito",
      goBack: "Volver",
      details: "Detalles",
      selectColor: "Seleccione un color",
      selectSize: "Seleccione una talla",
      noProducts: "No hay productos disponibles para esta subfamilia",
      code: "Código",
      relatedProducts: "Productos relacionados",
      fromSameSubfamily: "De la misma subfamilia",
      category: "Categoría",
      browse: "Ver todos",
      noProductsWithOption: "No hay productos disponibles con esta opción",
      addMoreForWholesale: "Añada {count} más para precio mayorista",
      addMoreForSpecial: "Añada {count} más para precio especial",
      quantity: "Cantidad",
      needHelp: "¿Necesitas ayuda?",
      askForAdvice: "Consultar por WhatsApp"
    },
    en: {
      color: "Color",
      size: "Size",
      price: "Price",
      customize: "Customize",
      retailPrice: "Retail Price",
      wholesalePrice: "Wholesale Price (12+)",
      specialPrice: "Special Price (24+)",
      addToCart: "Add to Cart",
      goBack: "Go Back",
      details: "Details",
      selectColor: "Select a color",
      selectSize: "Select a size",
      noProducts: "No products available for this subfamily",
      code: "Code",
      relatedProducts: "Related Products",
      fromSameSubfamily: "From the same subfamily",
      category: "Category",
      browse: "View all",
      noProductsWithOption: "No products available with this option",
      addMoreForWholesale: "Add {count} more for wholesale price",
      addMoreForSpecial: "Add {count} more for special price",
      quantity: "Quantity",
      needHelp: "Need help?",
      askForAdvice: "Ask via WhatsApp"
    }
  };
  
  const t = translations[lang] || translations.es;
  
  // Define fetchData outside of useEffect so it can be used elsewhere
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Validate parameters
      if (!subfamiliaId || isNaN(parseInt(subfamiliaId))) {
        throw new Error("ID de subfamilia inválido");
      }
      
      // Fetch subfamily with familia in a single query
      const { data: subfamiliaData, error: subfamiliaError } = await supabase
        .from('subfamilia')
        .select(`
          id, 
          nombre, 
          familia_id, 
          thumbnail,
          familia:familia_id (
            id,
            nombre
          )
        `)
        .eq('id', subfamiliaId)
        .single();
        
      if (subfamiliaError) throw subfamiliaError;
      setSubfamilia(subfamiliaData);
      
      // Set family information from nested query
      if (subfamiliaData.familia) {
        setFamilia(subfamiliaData.familia);
      }
      
      // Fetch all products with color info
      const { data: productosData, error: productosError } = await supabase
        .from('producto')
        .select(`
          id, 
          codigo, 
          nombre, 
          detalle, 
          precio1, 
          precio2, 
          precio3, 
          descuento, 
          color_numeric_id,
          color:color_numeric_id (name, rgb_code), 
          talla_id,
          sub_familia_id,
          iva_id
        `)
        .eq('sub_familia_id', subfamiliaId);
        
      if (productosError) throw productosError;
      setProductos(productosData || []);
      
      // Fetch images and inventory in parallel if we have products
      if (productosData && productosData.length > 0) {
        const productIds = productosData.map(p => p.id);
        
        const [imagesResult, inventarioResult] = await Promise.all([
          supabase
            .from('images')
            .select('url, producto_id')
            .in('producto_id', productIds),
          
          supabase
            .from('inventario')
            .select('producto_id, cantidad')
            .in('producto_id', productIds)
        ]);
        
        const { data: imagesData } = imagesResult;
        setProductImages(imagesData || []);
        
        const { data: inventarioData, error: inventarioError } = inventarioResult;
        if (inventarioError) throw inventarioError;
        
        // Create a map of product ID to inventory quantity
        const inventoryMap = {};
        inventarioData?.forEach(item => {
          if (!inventoryMap[item.producto_id]) {
            inventoryMap[item.producto_id] = 0;
          }
          inventoryMap[item.producto_id] += item.cantidad;
        });
        
        setInventoryMap(inventoryMap);
        
        // Set initial product and inventory
        const firstProduct = productosData[0];
        setSelectedProduct(firstProduct);
        
        if (firstProduct.color_numeric_id) {
          setSelectedColor(firstProduct.color_numeric_id);
        }
        
        if (firstProduct.talla_id) {
          setSelectedTalla(firstProduct.talla_id);
        }
        
        if (inventoryMap[firstProduct.id] !== undefined) {
          setAvailableInventory(inventoryMap[firstProduct.id]);
        }
      }
    } catch (error) {
      console.error('Error fetching product data:', error);
      
      // Set a more specific error message based on the error type
      if (error.code === '22P02') {
        setError('ID de subfamilia inválido');
      } else if (error.code === 'PGRST116') {
        setError('No se encontró la subfamilia');
      } else {
        setError(`Error cargando la información: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [subfamiliaId]);
  
  // Call fetchData in useEffect
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Get unique colors and sizes
  const uniqueColors = useMemo(() => {
    const colors = new Set(productos.map(p => p.color_numeric_id).filter(Boolean));
    return Array.from(colors);
  }, [productos]);
  
  const uniqueTallas = useMemo(() => {
    const tallas = new Set(productos.map(p => p.talla_id).filter(Boolean));
    return Array.from(tallas);
  }, [productos]);
  
  // Filter products based on selected color and size
  const filteredProducts = useMemo(() => {
    return productos.filter(p => 
      (!selectedColor || p.color_numeric_id === selectedColor) &&
      (!selectedTalla || p.talla_id === selectedTalla)
    );
  }, [productos, selectedColor, selectedTalla]);
  
  // Update selected product when filteredProducts changes
  useEffect(() => {
    if (filteredProducts.length > 0) {
      setSelectedProduct(filteredProducts[0]);
      
      // Update available inventory for the selected product
      if (inventoryMap[filteredProducts[0].id] !== undefined) {
        setAvailableInventory(inventoryMap[filteredProducts[0].id]);
      } else {
        setAvailableInventory(0);
      }
      
      // Find images for products with same subfamilia and color, then update main image
      // Image will be set by the consolidated useEffect
    }
  }, [filteredProducts, productImages, inventoryMap]);
  
  // Get images specifically for the selected individual product (for dots indicator)
  const currentProductImages = useMemo(() => {
    if (!selectedProduct) return [];
    return productImages.filter(img => img.producto_id === selectedProduct.id);
  }, [selectedProduct, productImages]);

  // Preload images in the background
  useEffect(() => {
    if (!currentProductImages.length) return;
    
    // Preload all images for the current product
    currentProductImages.forEach((img, index) => {
      // Skip the first image as it's already being displayed
      if (index === 0) return;
      
      const image = new Image();
      image.src = img.url;
    });
  }, [currentProductImages]);

  // Preload adjacent product images in background
  useEffect(() => {
    if (!productos.length || !selectedProduct) return;
    
    // Preload images for other color variations
    const adjacentProducts = productos.filter(p => 
      p.id !== selectedProduct.id && 
      p.sub_familia_id === selectedProduct.sub_familia_id
    );
    
    adjacentProducts.forEach(product => {
      const images = productImages.filter(img => img.producto_id === product.id);
      if (images.length > 0) {
        // Preload first image of each variant
        const image = new Image();
        image.src = images[0].url;
      }
    });
  }, [productos, selectedProduct, productImages]);

  // Consolidated effect to handle main image setting after all data is loaded
  useEffect(() => {
    if (!selectedProduct || !productImages.length || loading) return;

    // Find images for this specific product first
    const exactProductImages = productImages.filter(img => img.producto_id === selectedProduct.id);
    
    if (exactProductImages.length > 0) {
      // Use the first image of the exact product
      setMainImage(exactProductImages[0].url);
      setCurrentImageIndex(0);
    } else {
      // Fallback: Find images for same color and subfamily
      const sameColorImages = productImages.filter(img => {
        const imgProduct = productos.find(p => p.id === img.producto_id);
        return imgProduct && 
               imgProduct.sub_familia_id === selectedProduct.sub_familia_id &&
               imgProduct.color_numeric_id === selectedProduct.color_numeric_id;
      });
      
      if (sameColorImages.length > 0) {
        setMainImage(sameColorImages[0].url);
        setCurrentImageIndex(0);
      } else {
        // Final fallback: subfamily thumbnail
        setMainImage(subfamilia?.thumbnail || DEFAULT_PRODUCT_THUMB_URL);
        setCurrentImageIndex(0);
      }
    }
  }, [selectedProduct, productImages, productos, subfamilia, loading]);
  
  // Change product based on color selection
  const handleColorChange = (colorNumericId) => {
    setSelectedColor(colorNumericId);
    const matchingProduct = productos.find(p => 
      p.color_numeric_id === colorNumericId && 
      (!selectedTalla || p.talla_id === selectedTalla)
    );
    
    if (matchingProduct) {
      setSelectedProduct(matchingProduct);
      
      // Update available inventory
      if (inventoryMap[matchingProduct.id] !== undefined) {
        setAvailableInventory(inventoryMap[matchingProduct.id]);
      } else {
        setAvailableInventory(0);
      }
      
      // Image will be set by the useEffect
    }
  };
  
  // Change product based on size selection
  const handleTallaChange = (talla) => {
    setSelectedTalla(talla);
    const matchingProduct = productos.find(p => 
      p.talla_id === talla && 
      (!selectedColor || p.color_numeric_id === selectedColor)
    );
    
    if (matchingProduct) {
      setSelectedProduct(matchingProduct);
      
      // Update available inventory
      if (inventoryMap[matchingProduct.id] !== undefined) {
        setAvailableInventory(inventoryMap[matchingProduct.id]);
      } else {
        setAvailableInventory(0);
      }
      
      // Image will be set by the useEffect
    }
  };
  
  // Update the getPrice function to be more robust
  const getPrice = (product, qty) => {
    if (!product) return 0;
    
    // Parse quantity as integer to ensure proper comparison
    const quantity = parseInt(qty);
    
    // Use Number() to convert string values to numbers and provide fallbacks
    if (quantity >= 24 && product.precio3 !== null && product.precio3 !== undefined) {
      return Number(product.precio3) || 0;
    } else if (quantity >= 12 && product.precio2 !== null && product.precio2 !== undefined) {
      return Number(product.precio2) || 0;
    } else if (product.precio1 !== null && product.precio1 !== undefined) {
      return Number(product.precio1) || 0;
    }
    
    // Default fallback
    return 0;
  };
  
  // Update the handleAddToCart function to properly pass the quantity
  const handleAddToCart = () => {
    if (!selectedProduct) {
      alert('Por favor seleccione un producto');
      return;
    }

    // Check inventory limits
    if (availableInventory === 0) {
      alert('Lo sentimos, este producto está agotado.');
      return;
    }
    
    if (quantity > availableInventory) {
      alert(`Solo hay ${availableInventory} unidades disponibles.`);
      setQuantity(availableInventory);
      return;
    }

    // Make sure we're getting a valid price
    const priceValue = getPrice(selectedProduct, quantity);
    
    // Ensure price is a valid number
    if (isNaN(priceValue) || priceValue === null) {
      console.error('Invalid price value:', priceValue, 'for product:', selectedProduct);
      alert('Error: Invalid price. Please try again.');
      return;
    }
    
    // Convert quantity to integer to ensure it's a number, not a string
    const quantityValue = parseInt(quantity);
    if (isNaN(quantityValue) || quantityValue < 1) {
      console.error('Invalid quantity value:', quantity);
      alert('Error: Invalid quantity. Please try again.');
      return;
    }
    
    // Create a properly formatted product object for the cart
    addToCart({
      id: selectedProduct.id,
      codigo: selectedProduct.codigo,
      nombre: selectedProduct.detalle || selectedProduct.nombre,
      color_numeric_id: selectedColor, // Changed from color_id
      color_name: selectedProduct.color?.name || '', // Add color name
      talla_id: selectedTalla,
      // Include the original price fields
      precio1: selectedProduct.precio1,
      precio2: selectedProduct.precio2,
      precio3: selectedProduct.precio3,
      // Include IVA information
      iva_id: selectedProduct.iva_id,
      iva_porcentaje: selectedProduct.iva?.porcentaje,
      iva_nombre: selectedProduct.iva?.nombre,
      // Other fields
      price: priceValue,
      quantity: quantityValue,
      image: mainImage || '',
      imageUrl: mainImage || '',
      subfamilia_id: subfamiliaId,
      subfamilia_nombre: subfamilia?.nombre || ''
    });

    // Show pretty toast notification
    showAddToCartToast(quantityValue);
  };
  
  // Add this to calculate remaining items needed for next price break
  const getNextPriceBreakMessage = (product, qty, t) => {
    if (!product) return null;
    
    if (qty < 12 && product.precio2) {
      return t.addMoreForWholesale.replace('{count}', 12 - qty);
    } else if (qty >= 12 && qty < 24 && product.precio3) {
      return t.addMoreForSpecial.replace('{count}', 24 - qty);
    }
    return null;
  };
  
  // Add these to handle quantity changes
  const incrementQuantity = () => {
    if (quantity < availableInventory) {
      setQuantity(prev => prev + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0) {
      // Limit value to available inventory
      setQuantity(Math.min(value, availableInventory));
    } else if (e.target.value === '') {
      setQuantity('');
    }
  };

  const handleQuantityBlur = () => {
    if (quantity === '' || quantity < 1) {
      setQuantity(1);
    }
  };
  
  // Function to show the toast notification
  const showAddToCartToast = (quantity) => {
    const message = `¡${quantity} ${quantity > 1 ? 'productos agregados' : 'producto agregado'} al carrito!`;
    setToastMessage(message);
    setShowToast(true);
    
    // Auto dismiss after 2 seconds
    setTimeout(() => {
      setShowToast(false);
    }, 2000);
  };

  // Function to handle WhatsApp inquiry about stock
  const handleWhatsAppInquiry = () => {
    const productInfo = selectedProduct 
      ? `${selectedProduct.codigo} - ${selectedProduct.detalle || selectedProduct.nombre}`
      : subfamilia.nombre;
    
    const colorInfo = selectedProduct && selectedProduct.color?.name 
      ? ` (Color: ${selectedProduct.color.name})`
      : '';
    
    const sizeInfo = selectedTalla 
      ? ` (Talla: ${selectedTalla})`
      : '';
    
    const message = `Hola, me gustaría confirmar el inventario del producto: ${productInfo}${colorInfo}${sizeInfo}`;
    const whatsappUrl = `https://wa.me/50687839222?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  // Catalogue Mode: consult the price of this product via WhatsApp
  const handlePriceConsult = () => {
    const colorName = selectedProduct?.color?.name;
    const extra = [
      colorName ? ` (Color: ${colorName})` : '',
      selectedTalla ? ` (Talla: ${selectedTalla})` : '',
    ].join('');
    const url = priceConsultUrl({
      codigo: selectedProduct?.codigo,
      nombre: selectedProduct?.detalle || selectedProduct?.nombre || subfamilia?.nombre,
      extra,
    });
    window.open(url, '_blank');
  };
  
  // Swipe navigation for fullscreen modal
  const handleSwipeLeft = () => {
    if (currentProductImages.length > 1) {
      const nextIndex = currentImageIndex === currentProductImages.length - 1 ? 0 : currentImageIndex + 1;
      setCurrentImageIndex(nextIndex);
      setMainImage(currentProductImages[nextIndex].url);
    }
  };
  
  const handleSwipeRight = () => {
    if (currentProductImages.length > 1) {
      const prevIndex = currentImageIndex === 0 ? currentProductImages.length - 1 : currentImageIndex - 1;
      setCurrentImageIndex(prevIndex);
      setMainImage(currentProductImages[prevIndex].url);
    }
  };
  
  // Touch swipe detection
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  const handleTouchStartSwipe = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMoveSwipe = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const handleTouchEndSwipe = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      handleSwipeLeft();
    } else if (isRightSwipe) {
      handleSwipeRight();
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };
  
  if (loading) {
    return <div className="product-detail-loading">Cargando...</div>;
  }
  
  if (error) {
    return (
      <div className="product-detail-error">
        <p>{error}</p>
        <button 
          className="retry-button"
          onClick={() => {
            setError('');
            fetchData();
          }}
        >
          Intentar nuevamente
        </button>
        <button 
          className="back-button"
          onClick={() => navigate(-1)}
        >
          Volver atrás
        </button>
      </div>
    );
  }
  
  if (!subfamilia) {
    return <div className="product-detail-error">Producto no encontrado</div>;
  }
  
  return (
    <div className="product-detail-container">
      {/* Breadcrumb and Back Button in a flex container */}
      <div className="product-detail-header">
        {/* Left controls: Back button and breadcrumb navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button 
            className="product-detail-back-button" 
            onClick={() => navigate(-1)}
            style={{ marginRight: 8 }}
          >
            &larr; {t.goBack}
          </button>
          <div className="product-breadcrumb">
            <Link to="/">Home</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to="/catalogue">Catálogo</Link>
            {familia && (
              <>
                <span className="breadcrumb-separator">/</span>
                <Link to={`/catalogue?familia=${familia.id}`}>{familia.nombre}</Link>
              </>
            )}
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">{subfamilia.nombre}</span>
          </div>
        </div>
        {/* ...other right-side controls if any... */}
      </div>
      
      <div className="product-detail-content">
        <div className="product-detail-left">
          <div 
            className={`product-detail-main-image ${isZoomed ? 'zoom-active' : ''}`}
            onMouseMove={handleMouseMove}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={handleImageClick}
          >
            {/* Previous image button */}
            {currentProductImages.length > 1 && (
              <button 
                className="image-nav-btn prev-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  const prevIndex = currentImageIndex === 0 ? currentProductImages.length - 1 : currentImageIndex - 1;
                  setCurrentImageIndex(prevIndex);
                  setMainImage(currentProductImages[prevIndex].url);
                }}
              >
                &#8249;
              </button>
            )}
            
            {/* Zoom toggle button (desktop) or Fullscreen button (mobile) */}
            {!isMobile() ? (
              <button 
                className="zoom-toggle-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleZoom();
                }}
                title={isZoomed ? "Desactivar zoom" : "Activar zoom"}
              >
                {isZoomed ? '-' : '+'}
              </button>
            ) : (
              <button 
                className="fullscreen-toggle-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullscreen(true);
                  document.body.style.overflow = 'hidden';
                }}
                title="Ver en pantalla completa"
              >
                ⛶
              </button>
            )}
            
            <img 
              src={mainImage || DEFAULT_THUMB_URL}
              alt={selectedProduct?.nombre || subfamilia.nombre}
              loading="eager"
              decoding="async"
              style={{
                transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`
              }}
            />
            
            {/* Next image button */}
            {currentProductImages.length > 1 && (
              <button 
                className="image-nav-btn next-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  const nextIndex = currentImageIndex === currentProductImages.length - 1 ? 0 : currentImageIndex + 1;
                  setCurrentImageIndex(nextIndex);
                  setMainImage(currentProductImages[nextIndex].url);
                }}
              >
                &#8250;
              </button>
            )}
          </div>
          
          {/* Image indicators dots */}
          {currentProductImages.length > 1 && (
            <div className="image-indicators">
              {currentProductImages.map((_, index) => {
                // Show only 3 dots: previous, current, and next
                const shouldShow = currentProductImages.length <= 3 || 
                  Math.abs(index - currentImageIndex) <= 1;
                
                if (!shouldShow) return null;
                
                return (
                  <button
                    key={index}
                    className={`indicator-dot ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => {
                      setCurrentImageIndex(index);
                      setMainImage(currentProductImages[index].url);
                    }}
                  />
                );
              })}
            </div>
          )}
          
          {/* Download button (only for authenticated users) */}
          {user && mainImage && (
            <div className="image-download-section">
              <button 
                className="download-image-btn"
                onClick={handleDownloadImage}
                disabled={downloading}
                title="Descargar imagen"
              >
                {downloading ? (
                  <>
                    <span className="download-spinner"></span>
                    Descargando...
                  </>
                ) : (
                  <>
                    <span className="download-icon">⬇</span>
                    Descargar imagen
                  </>
                )}
              </button>
            </div>
          )}
        </div>
        
        <div className="product-detail-right">
          {familia && (
            <div className="product-detail-category">
              <span>{t.category}: </span>
              <Link to={`/catalogue?familia=${familia.id}`} className="category-link">
                {familia.nombre}
              </Link>
              <Link to={`/catalogue?familia=${familia.id}`} className="browse-all-link">
                {t.browse}
              </Link>
            </div>
          )}

          <h1 className="product-detail-title">
            {selectedProduct && selectedProduct.detalle ? selectedProduct.detalle : subfamilia.nombre}
          </h1>
          
          {selectedProduct && (
            <div className="product-detail-code">
              {t.code}: {selectedProduct.codigo}
            </div>
          )}
          
          {selectedProduct && selectedProduct.nombre && selectedProduct.nombre !== subfamilia.nombre && (
            <div className="product-detail-name">
              {selectedProduct.nombre}
            </div>
          )}
          
          {selectedProduct && (
            <div className="product-detail-prices">
              {catalogueMode ? (
                <button
                  className="product-detail-whatsapp-button"
                  onClick={handlePriceConsult}
                >
                  <i className="whatsapp-icon"></i> {lang === 'en' ? 'Ask for price on WhatsApp' : 'Consultar precio por WhatsApp'}
                </button>
              ) : (
                <>
                  <div className="price-item">
                    <span className="price-amount">₡ {getPrice(selectedProduct, quantity).toFixed(2)}</span>
                    <span className="price-label">
                      {quantity >= 24 ? t.specialPrice :
                       quantity >= 12 ? t.wholesalePrice :
                       t.retailPrice}
                    </span>
                  </div>

                  {/* Show price break message if applicable */}
                  {getNextPriceBreakMessage(selectedProduct, quantity, t) && (
                    <div className="price-thresholds">
                      <div className="threshold-message">
                        {getNextPriceBreakMessage(selectedProduct, quantity, t)}
                      </div>
                    </div>
                  )}
                </>
              )}
                {/* View all images button */}
                {currentProductImages.length > 1 && (
                  <button
                    className="view-all-images-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      openGallery();
                    }}
                  >
                    Ver todas las imágenes
                  </button>
                )}
            </div>
          )}

          {/* Customize section - Similar to the image */}
                {/* Gallery modal for all images */}
                {showGallery && (
                  <div className="gallery-modal" onClick={closeGallery}>
                    <div className="gallery-modal-content" onClick={e => e.stopPropagation()}>
                      <button className="gallery-close-btn" onClick={closeGallery}>×</button>
                      <h2>Todas las imágenes del producto</h2>
                      <div className="gallery-grid">
                        {currentProductImages.map((img, idx) => (
                          <img
                            key={idx}
                            src={img.url}
                            alt={`Imagen ${idx + 1}`}
                            className="gallery-image"
                            onClick={() => openGalleryFullscreen(idx)}
                            style={{ cursor: 'pointer' }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Fullscreen modal for gallery image */}
                {fullscreenGalleryIndex !== null && (
                  <div className="gallery-fullscreen-modal" onClick={closeGalleryFullscreen}>
                    <div className="gallery-fullscreen-content" onClick={e => e.stopPropagation()}>
                      <button className="gallery-fullscreen-close" onClick={closeGalleryFullscreen}>×</button>
                      {currentProductImages.length > 1 && (
                        <button className="gallery-fullscreen-prev" onClick={prevGalleryImage}>&#8249;</button>
                      )}
                      <img
                        src={currentProductImages[fullscreenGalleryIndex].url}
                        alt={`Imagen ${fullscreenGalleryIndex + 1}`}
                        className="gallery-fullscreen-image"
                      />
                      {currentProductImages.length > 1 && (
                        <button className="gallery-fullscreen-next" onClick={nextGalleryImage}>&#8250;</button>
                      )}
                    </div>
                  </div>
                )}
          <div className="product-customize">
            <h3 className="customize-heading">{t.customize}</h3>
            
            {uniqueColors.length > 0 && (
              <div className="product-detail-option">
                <h4 className="option-title">{t.color.toUpperCase()}</h4>
                <div className="color-options">
                  {uniqueColors.map(colorNumericId => {
                    // Find a product with this color to get the color details
                    const productWithColor = productos.find(p => p.color_numeric_id === colorNumericId);
                    const rgbCode = productWithColor?.color?.rgb_code || '#CCCCCC';
                    const colorName = productWithColor?.color?.name || '';
                    
                    // Check if this color option actually has a product
                    const hasProductWithColor = productos.some(p => 
                      p.color_numeric_id === colorNumericId && 
                      (!selectedTalla || p.talla_id === selectedTalla)
                    );
                    
                    return (
                      <button
                        key={colorNumericId}
                        className={`color-option ${selectedColor === colorNumericId ? 'active' : ''} ${!hasProductWithColor ? 'disabled' : ''}`}
                        onClick={() => hasProductWithColor && handleColorChange(colorNumericId)}
                        disabled={!hasProductWithColor}
                        title={colorName}
                      >
                        <span 
                          className={`color-swatch ${colorName.toLowerCase() === 'colores' ? 'rainbow-gradient' : ''}`}
                          style={colorName.toLowerCase() === 'colores' ? {} : { backgroundColor: rgbCode }}
                        ></span>
                        {selectedColor === colorNumericId && <span className="color-check">✓</span>}
                      </button>
                    );
                  })}
                </div>
                
                {/* Selected color name display */}
                {selectedColor && (
                  <div className="selected-color-label">
                    <span className="selected-label-text">
                      {t.color} seleccionado: <strong>
                        {productos.find(p => p.color_numeric_id === selectedColor)?.color?.name || 'N/A'}
                      </strong>
                    </span>
                  </div>
                )}
              </div>
            )}
            
            {uniqueTallas.length > 0 && (
              <div className="product-detail-option">
                <h4 className="option-title">Talla</h4>
                <div className="size-options">
                  {uniqueTallas.map(talla => {
                    // Check if this size option has a product
                    const hasProductWithTalla = productos.some(p => 
                      p.talla_id === talla && 
                      (!selectedColor || p.color_numeric_id === selectedColor)
                    );
                    
                    return (
                      <button
                        key={talla}
                        className={`size-option ${selectedTalla === talla ? 'active' : ''} ${!hasProductWithTalla ? 'disabled' : ''}`}
                        onClick={() => hasProductWithTalla && handleTallaChange(talla)}
                        disabled={!hasProductWithTalla}
                      >
                        {talla}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          
          {/* Quantity controls with inventory limit */}
          <div className="product-quantity-container">
            <div className="quantity-label-row">
              <span className="quantity-label">{t.quantity}</span>
              {availableInventory > 0 && (
                <span className="inventory-available">
                  {availableInventory} {availableInventory === 1 ? 'unidad disponible' : 'unidades disponibles'}
                </span>
              )}
            </div>
            
            <div className="quantity-add-container">
              <div className="quantity-controls">
                <button 
                  className="quantity-btn decrease" 
                  onClick={decrementQuantity}
                  disabled={quantity <= 1 || availableInventory === 0}
                >
                  -
                </button>
                <input 
                  type="text" 
                  value={quantity} 
                  onChange={handleQuantityChange}
                  onBlur={handleQuantityBlur}
                  min="1"
                  max={availableInventory}
                  className="quantity-input"
                  disabled={availableInventory === 0}
                />
                <button 
                  className="quantity-btn increase" 
                  onClick={incrementQuantity}
                  disabled={quantity >= availableInventory || availableInventory === 0}
                >
                  +
                </button>
              </div>
              
              {availableInventory === 0 ? (
                <button 
                  className="product-detail-whatsapp-button"
                  onClick={handleWhatsAppInquiry}
                >
                  <i className="whatsapp-icon"></i> Confirma inventario por WhatsApp
                </button>
              ) : (
                <button 
                  className="product-detail-add-button"
                  onClick={handleAddToCart}
                  disabled={!selectedProduct || quantity > availableInventory}
                >
                  <i className="cart-icon">🛒</i> {t.addToCart}
                </button>
              )}
            </div>
          </div>

          {/* WhatsApp Help Section */}
          <div className="product-help-container">
            <h4 className="help-title">{t.needHelp}</h4>
            <a 
              href={`https://wa.me/50687839222?text=Hola,%20me%20gustaría%20recibir%20asesoramiento%20sobre%20el%20producto:%20${encodeURIComponent(selectedProduct ? `${selectedProduct.codigo} - ${selectedProduct.detalle || selectedProduct.nombre}` : subfamilia.nombre)}`}
              className="whatsapp-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg className="whatsapp-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
              </svg>
              {t.askForAdvice}
            </a>
          </div>

          {/* Product metadata */}
          <div className="product-metadata">
            <div className="product-sku">
              <span>SKU: {selectedProduct?.codigo}</span>
            </div>
            <div className="product-category">
              <span>Categoría: </span>
              <Link to={`/catalogue?familia=${familia?.id}`}>
                {familia?.nombre}
              </Link>
            </div>
            <div className="product-browse-all">
              <Link to={`/catalogue?familia=${familia?.id}`} className="browse-all-button">
                {t.browse} {familia?.nombre}
              </Link>
            </div>
          </div>

        </div>
      </div>

      {/* Toast notification */}
      {showToast && (
        <div className="toast-notification">
          <div className="toast-content">
            <div className="toast-icon">✓</div>
            <div className="toast-message">{toastMessage}</div>
          </div>
        </div>
      )}
      
      {/* Fullscreen image modal for mobile */}
      {showFullscreen && (
        <div className={`fullscreen-modal ${showFullscreen ? 'active' : ''}`} onClick={closeFullscreen}>
          <div className="fullscreen-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="fullscreen-close" onClick={closeFullscreen}>
              ×
            </button>
            
            {/* Previous image button in fullscreen */}
            {currentProductImages.length > 1 && (
              <button 
                className="fullscreen-nav-btn prev-btn"
                onClick={handleSwipeRight}
              >
                &#8249;
              </button>
            )}
            
            <img 
              src={mainImage || DEFAULT_THUMB_URL}
              alt={selectedProduct?.nombre || subfamilia.nombre}
              onTouchStart={handleTouchStartSwipe}
              onTouchMove={handleTouchMoveSwipe}
              onTouchEnd={handleTouchEndSwipe}
            />
            
            {/* Next image button in fullscreen */}
            {currentProductImages.length > 1 && (
              <button 
                className="fullscreen-nav-btn next-btn"
                onClick={handleSwipeLeft}
              >
                &#8250;
              </button>
            )}
            
            {/* Image indicators in fullscreen */}
            {currentProductImages.length > 1 && (
              <div className="fullscreen-indicators">
                {currentProductImages.map((_, index) => {
                  // Show only 3 dots: previous, current, and next
                  const shouldShow = currentProductImages.length <= 3 || 
                    Math.abs(index - currentImageIndex) <= 1;
                  
                  if (!shouldShow) return null;
                  
                  return (
                    <button
                      key={index}
                      className={`fullscreen-indicator-dot ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={() => {
                        setCurrentImageIndex(index);
                        setMainImage(currentProductImages[index].url);
                      }}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductDetail;